import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import SearchFilterBar from "./SearchFilterBar";
import RecordingRow from "./RecordingRow";
import './RecordingList.css';
import { useAuthErrorHandler } from "../../hooks/useAuthErrorHandler";


const RecordingList = ({ refreshKey }) => {
  const API_ROUTE = process.env.REACT_APP_API_URL;
  const [recordings, setRecordings] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [transcriptToggles, setTranscriptToggles] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [audioUrls, setAudioUrls] = useState({});
  const [labelUpdateKey, setLabelUpdateKey] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showStarredOnly, setShowStarredOnly] = useState(false);
  const pollingRef = useRef(null);
  const navigate = useNavigate();
  const handleAuthError = useAuthErrorHandler();

  // Filtering logic
  const filteredRecordings = recordings.filter(
    rec =>
      (!showStarredOnly || rec.is_starred) &&
      (
        `${rec.user?.first_name} ${rec.user?.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.user?.organization?.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        rec.text.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  const fetchRecordings = useCallback(async () => {
  const token = localStorage.getItem("token");
  if (!token) {
    navigate("/login", { replace: true });
    return;
  }
  try {
    const response = await fetch(`${API_ROUTE}/audio/get_recordings`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (handleAuthError(response)) return;
    if (!response.ok) throw new Error('Failed to fetch recordings');
    let data = await response.json();
    data = data.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    setRecordings(data);
  } catch (err) {
    setError(err.message);
  } finally {
    setLoading(false);
  }
}, [API_ROUTE, handleAuthError, navigate]);

  useEffect(() => {
    fetchRecordings();
    return () => pollingRef.current && clearInterval(pollingRef.current);
  }, [API_ROUTE, refreshKey, navigate, fetchRecordings]);

  useEffect(() => {
    const hasProcessing = recordings.some(r => r.status === 'Processing');
    if (hasProcessing && !pollingRef.current) {
      pollingRef.current = setInterval(fetchRecordings, 6000);
    }
    if (!hasProcessing && pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, [recordings, fetchRecordings]);

  // Audio and transcript-related callbacks
  const fetchAudioBlobUrl = async (recordingId) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_ROUTE}/audio/get_audio/${recordingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (handleAuthError(response)) return;
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrls(prev => ({ ...prev, [recordingId]: url }));
    } catch (error) {
      console.error("Failed to fetch audio blob:", error);
    }
  };

  const updateSpeakerLabel = async (recordingId, speakerIndex, customLabel) => {
    const token = localStorage.getItem("token");
    try {
      await fetch(`${API_ROUTE}/audio/label_speaker/${recordingId}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ speaker_index: speakerIndex, custom_label: customLabel })
      });
      await fetchRecordings();
      setLabelUpdateKey(prev => prev + 1);
    } catch (err) {
      console.error("Failed to update speaker label:", err);
    }
  };

  const handleDeleteRecording = async (id) => {
    const token = localStorage.getItem("token");
    try {
      const response = await fetch(`${API_ROUTE}/audio/delete_recording/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (handleAuthError(response)) return;
      if (!response.ok) throw new Error("Failed to delete recording");
      setRecordings(prev => prev.filter(r => r.recording_id !== id));
      setExpandedRow(null);
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete recording.");
    }
  };

  // Render
  if (loading) return <p>Loading recordings...</p>;
  if (error) return <p>Error: {error}</p>;

  return (
    <div className="recordings-container">
      <div className="recordings-table-wrapper">
        <SearchFilterBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          showStarredOnly={showStarredOnly}
          setShowStarredOnly={setShowStarredOnly}
        />

        {filteredRecordings.length === 0 ? (
          <div className="recordings-empty-state">
            <span role="img" aria-label="microphone" style={{ fontSize: "2.1em", display: "block", marginBottom: "0.45em" }}>🎙️</span>
            No recordings found.<br />Try another search or upload a new one!
          </div>
        ) : (
          <table className="recordings-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Org</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRecordings.map((rec) => (
                <RecordingRow
                  key={rec.id}
                  rec={rec}
                  expandedRow={expandedRow}
                  setExpandedRow={setExpandedRow}
                  transcriptToggles={transcriptToggles}
                  setTranscriptToggles={setTranscriptToggles}
                  fetchAudioBlobUrl={fetchAudioBlobUrl}
                  audioUrls={audioUrls}
                  labelUpdateKey={labelUpdateKey}
                  updateSpeakerLabel={updateSpeakerLabel}
                  handleDeleteRecording={handleDeleteRecording}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default RecordingList;
